import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { BlockNoteEditor, PartialBlock } from "@blocknote/core";
import { BlockNoteView } from "@blocknote/mantine";
import { useCreateBlockNote } from "@blocknote/react";
import "@blocknote/mantine/style.css";
import { useTheme } from "@/lib/theme-provider";
import { api } from "@/lib/api-client";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";

interface EditorProps {
  initialContent?: string;
  onChange?: (content: string) => void;
  onSave?: (content: string) => void;
  editable?: boolean;
  placeholder?: string;
}

export function Editor({
  initialContent,
  onChange,
  onSave,
  editable = true,
  placeholder = "Escribe algo o presiona '/' para comandos...",
}: EditorProps) {
  const { resolvedTheme } = useTheme();
  const [isReady, setIsReady] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const editorRef = useRef<BlockNoteEditor | null>(null);
  const lastContentRef = useRef<string>("");
  const onChangeRef = useRef(onChange);
  const onSaveRef = useRef(onSave);

  // Actualizar refs de callbacks
  useEffect(() => {
    onChangeRef.current = onChange;
    onSaveRef.current = onSave;
  }, [onChange, onSave]);

  // Parse initial content
  const initialBlocks = useMemo<PartialBlock[]>(() => {
    if (!initialContent) {
      return [
        {
          type: "paragraph",
          content: placeholder,
        },
      ];
    }

    try {
      const parsed = JSON.parse(initialContent);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      if (parsed.blocks) {
        return parsed.blocks;
      }
    } catch {
      return undefined;
    }

    return [
      {
        type: "paragraph",
        content: placeholder,
      },
    ];
  }, [initialContent, placeholder]);

  // Check if content is markdown (not JSON)
  const isMarkdownContent = useMemo(() => {
    if (!initialContent) return false;
    try {
      JSON.parse(initialContent);
      return false;
    } catch {
      return initialContent.trim().length > 0;
    }
  }, [initialContent]);

  // Create editor
  const editor: BlockNoteEditor = useCreateBlockNote({
    initialContent: initialBlocks,
    uploadFile: async (file: File): Promise<string> => {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const data = await api.post<{ success?: boolean; url?: string }>('/upload', formData);
        if (data.success && data.url) {
          return data.url;
        }
      } catch (error) {
        console.error("Upload error:", error);
      }

      return URL.createObjectURL(file);
    },
  });

  // Guardar referencia al editor
  useEffect(() => {
    if (editor) {
      editorRef.current = editor;
      console.log("[Editor] Editor instance stored");
    }
  }, [editor]);

  // Función para obtener contenido actual
  const getCurrentContent = useCallback(() => {
    const currentEditor = editorRef.current;
    if (!currentEditor) return null;
    
    try {
      const blocks = currentEditor.document;
      return JSON.stringify(blocks);
    } catch (error) {
      console.error("[Editor] Error getting content:", error);
      return null;
    }
  }, []);

  // Función para guardar
  const handleSave = useCallback(() => {
    const content = getCurrentContent();
    if (content && content !== lastContentRef.current) {
      console.log("[Editor] Saving document...");
      onSaveRef.current?.(content);
      lastContentRef.current = content;
      setHasChanges(false);
    }
  }, [getCurrentContent]);

  // Polling para detectar cambios (cada 500ms)
  useEffect(() => {
    if (!isReady) return;

    console.log("[Editor] Starting content polling");
    
    const interval = setInterval(() => {
      const content = getCurrentContent();
      if (content && content !== lastContentRef.current) {
        console.log("[Editor] Content changed detected via polling");
        onChangeRef.current?.(content);
        setHasChanges(true);
        
        // Auto-save después de 2 segundos sin cambios
        const timeout = setTimeout(() => {
          handleSave();
        }, 2000);
        
        return () => clearTimeout(timeout);
      }
    }, 500);

    return () => {
      console.log("[Editor] Stopping content polling");
      clearInterval(interval);
    };
  }, [isReady, getCurrentContent, handleSave]);

  // Guardar contenido inicial
  useEffect(() => {
    if (isReady && editorRef.current) {
      const content = getCurrentContent();
      if (content) {
        lastContentRef.current = content;
      }
    }
  }, [isReady, getCurrentContent]);

  // Convert markdown to blocks when editor is ready
  useEffect(() => {
    if (editor && isMarkdownContent && initialContent && isReady) {
      const convertMarkdown = async () => {
        try {
          const blocks = await editor.tryParseMarkdownToBlocks(initialContent);
          if (blocks && blocks.length > 0) {
            editor.replaceBlocks(editor.document, blocks);
            // Actualizar referencia después de convertir
            setTimeout(() => {
              const content = getCurrentContent();
              if (content) {
                lastContentRef.current = content;
              }
            }, 100);
          }
        } catch (error) {
          console.error("Error converting markdown:", error);
        }
      };
      convertMarkdown();
    }
  }, [editor, isMarkdownContent, initialContent, isReady, getCurrentContent]);

  // Mark as ready después de que el editor se monte
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log("[Editor] Marking as ready");
      setIsReady(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="editor-container min-h-[60vh] h-full relative">
      <BlockNoteView
        editor={editor}
        editable={editable}
        theme={resolvedTheme === "dark" ? "dark" : "light"}
        className="min-h-[60vh] h-full"
        formattingToolbar={true}
        slashMenu={true}
        sideMenu={true}
      />
      
      {/* Botón de guardar manual */}
      {hasChanges && (
        <div className="absolute bottom-4 right-4">
          <Button
            onClick={handleSave}
            size="sm"
            className="shadow-lg"
          >
            <Save className="w-4 h-4 mr-2" />
            Guardar
          </Button>
        </div>
      )}
    </div>
  );
}
